import React, { useContext, createContext } from "react";
import {
  authenticationTokenExpiresIn,
  getAuthenticationToken,
  tokenPayload,
} from "../auth";
import { useCurrentLocation } from "../components/LocationSelector";

const AuthenticationTokenContext = createContext();

export function useAuthenticationToken() {
  const context = useContext(AuthenticationTokenContext);

  return [context?.token, context?.setToken];
}

export function useAuthData() {
  return useContext(AuthenticationTokenContext);
}

/**
 * @returns {Set<string>}
 */
export function useRoles() {
  const location = useCurrentLocation();
  const authData = useAuthData();

  return new Set(authData.roles?.[location] ?? []);
}

export function AuthenticationTokenProvider({ children }) {
  const [token, setToken] = React.useState(getAuthenticationToken());

  const value = React.useMemo(() => {
    const decodedToken = tokenPayload(token);

    return {
      token,
      setToken,
      id: decodedToken?.id,
      roles: decodedToken?.roles || {},
      first_name: decodedToken?.first_name,
      last_name: decodedToken?.last_name,
      username: decodedToken?.username,
    };
  }, [token]);

  React.useEffect(
    function removeExpiredToken() {
      if (token == null) return;

      const timeoutId = setTimeout(() => {
        setToken(null);
      }, authenticationTokenExpiresIn(token));

      return () => clearTimeout(timeoutId);
    },
    [token],
  );

  return (
    <AuthenticationTokenContext.Provider value={value}>
      {children}
    </AuthenticationTokenContext.Provider>
  );
}

export { AuthenticationTokenContext };
